import { BarChart3, FileText, FolderOpen, LayoutDashboard, MessageSquare, Newspaper, Phone, UserCog, Menu, ChevronLeft, X, ClipboardList, Shield, FileCheck, Calendar, ChevronDown, ChevronRight, UserCheck, BookOpen } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ROUTE_PATH from '../../constants/routes';
import { isPathDisabled } from '../../utils/routeRedirectUtils';
import { usePermission } from '../../hooks/usePermission';

export const sidebarMenuItems = [
    {
        id: 'overview',
        label: 'Tổng quan',
        icon: LayoutDashboard,
        path: ROUTE_PATH.DASHBOARD,
        hasSubmenu: false,
        requiresPermission: false
    },
    {
        id: 'reports',
        label: 'Phản ánh kiến nghị',
        icon: MessageSquare,
        path: ROUTE_PATH.REPORT,
        hasSubmenu: true,
        modulePrefixes: ['PA', 'LVPA'],
        submenu: [
            {
                id: 'reports-list',
                label: 'Quản lý phản ánh',
                path: ROUTE_PATH.REPORT,
            },
            {
                id: 'reports-extensions',
                label: 'Quản lý gia hạn',
                path: ROUTE_PATH.REPORT_EXTENSIONS,
            }
        ]
    },
    {
        id: 'report-areas',
        label: 'Quản lý lĩnh vực phản ánh',
        icon: ClipboardList,
        path: ROUTE_PATH.REPORT_AREAS,
        hasSubmenu: false,
        modulePrefix: 'LVPA'
    },
    {
        id: 'news',
        label: 'Tin tức & Thông báo',
        icon: Newspaper,
        path: ROUTE_PATH.NEWS,
        hasSubmenu: false,
        modulePrefix: 'TTIN'
    },
    {
        id: 'category-news',
        label: 'Danh mục tin tức',
        icon: Newspaper,
        path: ROUTE_PATH.CATEGORY_NEWS,
        hasSubmenu: false,
        modulePrefix: 'DMTT'
    },
    {
        id: 'areas',
        label: 'Quản lý lĩnh vực thủ tục',
        icon: FileText,
        path: ROUTE_PATH.AREAS,
        hasSubmenu: false,
        modulePrefix: 'LVTTHC'
    },
    {
        id: 'procedures',
        label: 'Thủ tục hành chính',
        icon: FileText,
        path: ROUTE_PATH.PROCEDURES,
        hasSubmenu: false,
        modulePrefix: 'TT'
    },
    {
        id: 'templates',
        label: 'Biểu mẫu',
        icon: FolderOpen,
        path: ROUTE_PATH.TEMPLATES,
        hasSubmenu: false,
        modulePrefix: 'MD'
    },
    {
        id: 'documents',
        label: 'Quản lý tài liệu',
        icon: BookOpen,
        path: ROUTE_PATH.DOCUMENTS,
        hasSubmenu: true,
        modulePrefix: 'TL',
        submenu: [
            { id: 'history', label: 'Văn hóa - Lịch sử', path: ROUTE_PATH.DOCUMENTS_HISTORY },
            { id: 'legal', label: 'Quy phạm pháp luật', path: ROUTE_PATH.DOCUMENTS_LEGAL },
            { id: 'categories', label: 'Danh mục loại văn bản', path: ROUTE_PATH.DOCUMENTS_CATEGORIES },
        ]
    },
    {
        id: 'government',
        label: 'Quản lý cơ sở dịch vụ công',
        icon: FolderOpen,
        path: ROUTE_PATH.GOVERNMENT,
        hasSubmenu: false,
        modulePrefixes: ['UB', 'CSV']
    },
    {
        id: 'contact',
        label: 'Ủy ban Phường',
        icon: Phone,
        path: ROUTE_PATH.CONTACT,
        hasSubmenu: false,
        modulePrefix: 'UB'
    },
    {
        id: 'officer-reception-management',
        label: 'Quản lý lịch tiếp công dân',
        icon: Calendar,
        path: ROUTE_PATH.SCHEDULES,
        hasSubmenu: false,
        modulePrefix: 'LTD'
    },
    {
        id: 'leader-meeting-management',
        label: 'Quản lý lịch gặp lãnh đạo',
        icon: UserCheck,
        hasSubmenu: true,
        modulePrefixes: ['LMR', 'LMS', 'LMRT', 'LTD'],
        submenu: [
            {
                id: 'leader-meeting-feedback',
                label: 'Danh sách đơn gặp lãnh đạo',
                path: ROUTE_PATH.LEADER_MEETING_FEEDBACK,
            },
            {
                id: 'add-leader-schedule',
                label: 'Thêm lịch',
                path: ROUTE_PATH.ADD_LEADER_SCHEDULE,
            }
        ]
    },
    {
        id: 'statistics',
        label: 'Báo cáo & Thống kê',
        icon: BarChart3,
        path: ROUTE_PATH.STATISTICS,
        hasSubmenu: false,
        modulePrefix: 'RPT'
    },
    {
        id: 'accounts',
        label: 'Quản lý tài khoản',
        icon: UserCog,
        path: ROUTE_PATH.ACCOUNTS,
        hasSubmenu: false,
        modulePrefix: 'ND'
    },
    {
        id: 'permissions',
        label: 'Quản lý quyền truy cập',
        icon: Shield,
        path: ROUTE_PATH.PERMISSIONS,
        hasSubmenu: false,
        modulePrefixes: ['ROLE', 'PERM']
    },
    {
        id: 'audit-log',
        label: 'Chi tiết hoạt động',
        icon: FileCheck,
        path: ROUTE_PATH.AUDIT_LOG,
        hasSubmenu: false,
        modulePrefix: 'ADL'
    }
];

export default function Sidebar({ collapsed, onToggle, onMobileClose }) {
    const location = useLocation();
    const { canAccessModule } = usePermission();
    const [openSubmenus, setOpenSubmenus] = useState(() => {
        const initial = {
            'reports': true,
            'officer-reception-management': true,
            'leader-meeting-management': true,
            'documents': true
        };
        sidebarMenuItems.forEach(item => {
            if (item.submenu && item.submenu.some(sub => location.pathname === sub.path || (item.path && location.pathname === item.path))) {
                initial[item.id] = true;
            }
        });
        return initial;
    });

    const toggleSubmenu = (id) => {
        if (collapsed && onToggle) {
            onToggle();
        }
        setOpenSubmenus(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const handleLinkClick = () => {
        if (window.innerWidth < 768 && onMobileClose) {
            onMobileClose();
        }
    };

    const visibleMenuItems = React.useMemo(() => {
        return sidebarMenuItems.filter((item) => {
            if (item.requiresPermission === false) {
                return true;
            }

            if (item.modulePrefix) {
                return canAccessModule(item.modulePrefix);
            }

            if (item.modulePrefixes && Array.isArray(item.modulePrefixes)) {
                return item.modulePrefixes.some(prefix => canAccessModule(prefix));
            }

            return false;
        });
    }, [canAccessModule]);

    const [isScrolled, setIsScrolled] = useState(false);

    const handleScroll = (e) => {
        setIsScrolled(e.target.scrollTop > 0);
    };

    return (
        <aside
            className={`
                bg-white border-r border-gray-200 h-screen flex flex-col overflow-hidden
                transition-all duration-300 ease-in-out flex-shrink-0
                ${collapsed ? 'w-20' : 'w-64'}
            `}
        >
            <div className={`sticky top-0 z-20 shrink-0 border-b border-gray-200 bg-white transition-all duration-300 relative h-[66px] flex items-center ${collapsed ? 'px-3' : 'px-4'}`}>
                <div className="flex items-center justify-between w-full">
                    {!collapsed && (
                        <div className="flex-1 pr-2">
                            <h3 className="text-base font-bold text-gray-800 leading-tight">
                                SOS Khu Phố<br />Tăng Nhơn Phú
                            </h3>
                        </div>
                    )}

                    <button
                        onClick={onToggle}
                        className={`
                            hidden md:block
                            p-2 rounded-lg hover:bg-gray-100 transition-colors
                            ${collapsed ? 'mx-auto' : 'ml-auto'}
                        `}
                        title={collapsed ? 'Mở rộng sidebar' : 'Thu gọn sidebar'}
                    >
                        {collapsed ? (
                            <Menu className="w-5 h-5 text-gray-600" />
                        ) : (
                            <ChevronLeft className="w-5 h-5 text-gray-600" />
                        )}
                    </button>

                    <button
                        onClick={onMobileClose}
                        className={`
                            md:hidden
                            p-2 rounded-lg hover:bg-gray-100 transition-colors
                            ${collapsed ? 'mx-auto' : 'ml-auto'}
                        `}
                        title="Đóng menu"
                    >
                        <X className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {/* Bottom Shadow Overlay */}
                <div
                    className={`absolute top-full left-0 right-0 h-4 bg-gradient-to-b from-black/10 to-transparent pointer-events-none transition-opacity duration-300 ease-in-out ${
                        isScrolled ? "opacity-100" : "opacity-0"
                    }`}
                />
            </div>

            <nav
                onScroll={handleScroll}
                className={`flex-1 overflow-y-auto sidebar-scroll py-4 transition-all duration-300 ${collapsed ? 'px-2' : 'px-3'}`}
            >
                <ul className="space-y-1.5">
                    {visibleMenuItems.map((item) => {
                        const Icon = item.icon;

                        if (item.submenu) {
                            const isSubOpen = openSubmenus[item.id];
                            const isSubActive = item.submenu.some(sub => location.pathname === sub.path);

                            return (
                                <li key={item.id}>
                                    <div
                                        onClick={() => toggleSubmenu(item.id)}
                                        className={`
                                            flex items-center text-sm rounded-lg transition-all duration-200 cursor-pointer
                                            ${collapsed ? 'px-3 py-3 justify-center' : 'px-4 py-3'}
                                            ${isSubActive ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700 hover:bg-gray-50'}
                                        `}
                                        title={collapsed ? item.label : ''}
                                    >
                                        <Icon
                                            className={`
                                                w-5 h-5 flex-shrink-0
                                                ${isSubActive ? 'text-blue-600' : 'text-gray-500'}
                                                ${collapsed ? '' : 'mr-3'}
                                            `}
                                        />
                                        {!collapsed && (
                                            <>
                                                <span className="font-medium flex-1">{item.label}</span>
                                                {isSubOpen ? (
                                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                                ) : (
                                                    <ChevronRight className="w-4 h-4 text-gray-400" />
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {!collapsed && isSubOpen && (
                                        <ul className="mt-1 ml-6 space-y-1 border-l-2 border-gray-100 pl-3">
                                            {item.submenu.map(sub => {
                                                const isChildActive = location.pathname === sub.path;
                                                return (
                                                    <li key={sub.id}>
                                                        <Link
                                                            to={sub.path}
                                                            onClick={handleLinkClick}
                                                            className={`
                                                                block text-xs rounded-lg px-3 py-2 transition-all duration-200
                                                                ${isChildActive
                                                                    ? 'bg-blue-50 text-blue-600 font-bold'
                                                                    : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600'
                                                                }
                                                            `}
                                                        >
                                                            {sub.label}
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </li>
                            );
                        }

                        const isActive = location.pathname === item.path;
                        const isDisabled = isPathDisabled(item.path);

                        return (
                            <li key={item.id}>
                                {isDisabled ? (
                                    <div
                                        className={`
                                            flex items-center text-sm rounded-lg transition-all duration-200 cursor-not-allowed
                                            ${collapsed ? 'px-3 py-3 justify-center' : 'px-4 py-3'}
                                            opacity-50 bg-gray-50 text-gray-400
                                            hover:opacity-60
                                        `}
                                        title={`${item.label} (Chức năng chưa khả dụng)`}
                                    >
                                        <Icon
                                            className={`
                                                w-5 h-5 flex-shrink-0 text-gray-400
                                                ${collapsed ? '' : 'mr-3'}
                                            `}
                                        />

                                        {!collapsed && (
                                            <span className="font-medium flex-1">{item.label}</span>
                                        )}
                                    </div>
                                ) : (
                                    <Link
                                        to={item.path}
                                        onClick={handleLinkClick}
                                        className={`
                                            flex items-center text-sm rounded-lg transition-all duration-200
                                            ${collapsed ? 'px-3 py-3 justify-center' : 'px-4 py-3'}
                                            ${isActive
                                                ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600 font-medium'
                                                : 'text-gray-700 hover:bg-gray-50'
                                            }
                                        `}
                                        title={collapsed ? item.label : ''}
                                    >
                                        <Icon
                                            className={`
                                                w-5 h-5 flex-shrink-0
                                                ${isActive ? 'text-blue-600' : 'text-gray-500'}
                                                ${collapsed ? '' : 'mr-3'}
                                            `}
                                        />

                                        {!collapsed && (
                                            <span className="font-medium flex-1">{item.label}</span>
                                        )}
                                    </Link>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </nav>

            <div className={`shrink-0 border-t border-gray-200 bg-white transition-all duration-300 ${collapsed ? 'p-2' : 'p-3'}`}>
                <p className={`text-xs text-gray-400 text-center ${collapsed ? 'transform rotate-90' : ''}`}>
                    {collapsed ? 'v1' : 'v1.0.0'}
                </p>
            </div>
        </aside>
    );
}

Sidebar.propTypes = {
    collapsed: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    onMobileClose: PropTypes.func
};
